import React, { createContext, useContext, useState, useEffect } from "react";
import apiClient from "utils/apiClient";
import AuthLoading from "components/common/AuthLoading";
import usePrivileges from "hooks/access-control/usePrivileges";
import useRoles from "hooks/access-control/useRoles";
import { getUserEffectivePrivileges } from "helpers/rbac";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const { fetchUserRoles, fetchRolePrivileges } = useRoles();
  const { fetchUserPrivileges } = usePrivileges();
  const [user, setUser] = useState(null); // will hold FULL profile now
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialCheck, setIsInitialCheck] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [userPrivileges, setUserPrivileges] = useState([]);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const storedToken = sessionStorage.getItem("authToken");

      if (!storedToken) {
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        setIsInitialCheck(false);
        return;
      }

      setAuthToken(storedToken);

      // Verify token and get basic identity
      const identityRes = await apiClient.get(
        "/identity-service/api/v1/users/me",
        {
          headers: { Authorization: `Bearer ${storedToken}` },
        }
      );

      if (identityRes.data && identityRes.data.id) {
        // Fetch FULL profile after identity confirmed
        const profileRes = await apiClient.get(
          "/profile-service/api/v1/user-profiles/get?refresh=true",
          {
            headers: { Authorization: `Bearer ${storedToken}` },
          }
        );

        if (profileRes.data && profileRes.data.id) {
          setUser(profileRes.data);
          setIsAuthenticated(true);

          // fetch privileges when refreshing auth state
          try {
            const allPrivileges = await getUserEffectivePrivileges(
              profileRes.data.id,
              fetchUserPrivileges,
              fetchUserRoles,
              fetchRolePrivileges
            );

            setUserPrivileges(allPrivileges);
          } catch (err) {
            console.error("Failed to fetch privileges on refresh:", err);
          }
        } else {
          // Profile fetch failed, fallback to identity or logout
          setUser(identityRes.data);
          setIsAuthenticated(true);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      // Handle errors, e.g. token invalid or expired
      if (error.response?.status === 401) {
        sessionStorage.removeItem("authToken");
        setAuthToken(null);
      }
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
      setIsInitialCheck(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await apiClient.post(
        "/identity-service/api/v1/auth/login",
        { email, password }
      );

      if (response.data && response.data.accessToken) {
        const { accessToken } = response.data;
        sessionStorage.setItem("authToken", accessToken);
        setAuthToken(accessToken);

        // Get FULL profile right after login
        const profileRes = await apiClient.get(
          "/profile-service/api/v1/user-profiles/get?refresh=true",
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        if (profileRes.data && profileRes.data.id) {
          setUser(profileRes.data);
          setIsAuthenticated(true);
          return { success: true, data: profileRes.data };
        } else {
          // fallback to identity/me if profile fails
          const identityRes = await apiClient.get(
            "/identity-service/api/v1/users/me",
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );
          setUser(identityRes.data);
          setIsAuthenticated(true);

          // fetch user privileges after login
          try {
            const allPrivileges = await getUserEffectivePrivileges(
              profileRes.data.id,
              fetchUserPrivileges,
              fetchUserRoles,
              fetchRolePrivileges
            );

            setUserPrivileges(allPrivileges);
          } catch (err) {
            console.error("Failed to fetch user privileges on login:", err);
          }
          return { success: true, data: identityRes.data };
        }
      } else {
        return { success: false, error: "No access token in response" };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post("/api/v1/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setAuthToken(null);
      sessionStorage.removeItem("authToken");
    }
    return true;
  };

  const isLoggedIn = () => !!(isAuthenticated && user);

  const refreshAuth = async () => {
    try {
      await checkAuthStatus();
    } catch (error) {
      console.error("Failed to refresh authentication:", error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    isInitialCheck,
    login,
    logout,
    checkAuthStatus,
    isLoggedIn,
    refreshAuth,
    privileges: userPrivileges,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <AuthLoading /> : children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
